
import { supabase } from '@/integrations/supabase/client';

export interface Organization {
  id: string;
  name: string;
  domain?: string;
  max_team_members?: number;
  created_by: string;
  created_at: string;
  updated_at: string;
  member_count?: number;
}

export async function getOrganizations(): Promise<Organization[]> {
  const { data, error } = await supabase
    .from('organizations')
    .select(`
      *,
      organization_members!inner(count)
    `)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching organizations:', error);
    throw error;
  }

  return (data || []).map(org => ({
    ...org,
    member_count: org.organization_members?.length || 0
  }));
}

export async function createOrganization(orgData: {
  name: string;
  domain?: string;
  max_team_members?: number;
  created_by: string;
}) {
  const { data, error } = await supabase
    .from('organizations')
    .insert([orgData])
    .select()
    .single();

  if (error) {
    console.error('Error creating organization:', error);
    throw error;
  }

  return data;
}

export async function updateOrganization(id: string, updates: {
  name?: string;
  domain?: string;
  max_team_members?: number;
}) {
  const { data, error } = await supabase
    .from('organizations')
    .update({
      ...updates,
      updated_at: new Date().toISOString()
    })
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('Error updating organization:', error);
    throw error;
  }

  return data;
}

export async function deleteOrganization(id: string) {
  // Check if organization has members
  const { data: members, error: membersError } = await supabase
    .from('organization_members')
    .select('id')
    .eq('organization_id', id);

  if (membersError) {
    console.error('Error checking organization members:', membersError);
    throw membersError;
  }

  if (members && members.length > 0) {
    throw new Error('Cannot delete organization with existing members. Please remove all members first.');
  }

  // Delete the organization
  const { error } = await supabase
    .from('organizations')
    .delete()
    .eq('id', id);

  if (error) {
    console.error('Error deleting organization:', error);
    throw error;
  }
}

export async function getOrganizationStats() {
  // Get total organizations
  const { data: orgsData, error: orgsError } = await supabase
    .from('organizations')
    .select('id');

  if (orgsError) {
    console.error('Error fetching organization stats:', orgsError);
    throw orgsError;
  }

  // Get total members across all organizations
  const { data: membersData, error: membersError } = await supabase
    .from('organization_members')
    .select('id');

  if (membersError) {
    console.error('Error fetching member stats:', membersError);
    throw membersError;
  }

  return {
    totalOrganizations: orgsData?.length || 0,
    totalMembers: membersData?.length || 0,
  };
}
